import type {
  Insight,
  InsightCategory,
  InsightDetailResponse,
  ListInsightsResponse,
  ListSourcesResponse,
  ListActivityResponse,
  TelegramActivity,
  Source,
  CreateSourceRequest,
  UpdateSourceRequest,
  UpdateInsightRequest,
  ReviewAction,
  SearchIndexEntry,
  SourceType,
  DigestRecord,
  AdvisorPlan,
} from '@claude-code-insights/shared';

const API_BASE = import.meta.env.VITE_API_URL || '';

let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function fetchJson<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
  if (response.status === 401) {
    onUnauthorized?.();
    throw new Error('Unauthorized');
  }
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error((error as { error: string }).error || response.statusText);
  }
  if (response.status === 204) return null as T;
  return response.json() as Promise<T>;
}

// ─── Insights ───────────────────────────────────────────────────────────────

export async function fetchInsights(params?: {
  category?: InsightCategory;
  sourceType?: SourceType;
  minRelevance?: number;
  bookmarked?: boolean;
  sortBy?: 'createdAt' | 'relevanceScore' | 'title';
  sortDirection?: 'asc' | 'desc';
  status?: string;
  limit?: number;
  cursor?: string;
}): Promise<ListInsightsResponse> {
  const qs = new URLSearchParams();
  if (params?.category) qs.set('category', params.category);
  if (params?.sourceType) qs.set('sourceType', params.sourceType);
  if (params?.minRelevance) qs.set('minRelevance', String(params.minRelevance));
  if (params?.bookmarked) qs.set('bookmarked', 'true');
  if (params?.sortBy) qs.set('sortBy', params.sortBy);
  if (params?.sortDirection) qs.set('sortDirection', params.sortDirection);
  if (params?.status) qs.set('status', params.status);
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.cursor) qs.set('cursor', params.cursor);

  const query = qs.toString();
  return fetchJson<ListInsightsResponse>(
    `/api/insights${query ? `?${query}` : ''}`,
  );
}

export async function fetchInsight(id: string): Promise<InsightDetailResponse> {
  return fetchJson<InsightDetailResponse>(`/api/insights/${id}`);
}

export async function fetchReviewQueue(params?: {
  limit?: number;
  cursor?: string;
}): Promise<ListInsightsResponse> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.cursor) qs.set('cursor', params.cursor);

  const query = qs.toString();
  return fetchJson<ListInsightsResponse>(
    `/api/insights/review${query ? `?${query}` : ''}`,
  );
}

export async function updateInsight(
  id: string,
  data: UpdateInsightRequest,
): Promise<Insight> {
  return fetchJson<Insight>(`/api/insights/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function reviewInsight(
  id: string,
  action: ReviewAction,
): Promise<void> {
  await fetchJson(`/api/insights/${id}/review`, {
    method: 'PATCH',
    body: JSON.stringify(action),
  });
}

// ─── Sources ────────────────────────────────────────────────────────────────

export async function fetchSources(): Promise<ListSourcesResponse> {
  return fetchJson<ListSourcesResponse>('/api/sources');
}

export async function createSource(
  data: CreateSourceRequest,
): Promise<Source> {
  return fetchJson<Source>('/api/sources', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSource(
  id: string,
  data: UpdateSourceRequest,
): Promise<Source> {
  return fetchJson<Source>(`/api/sources/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(data),
  });
}

export async function deleteSource(id: string): Promise<void> {
  await fetchJson(`/api/sources/${id}`, { method: 'DELETE' });
}

export async function pollSource(id: string): Promise<{ ok: boolean }> {
  return fetchJson<{ ok: boolean }>(`/api/sources/${id}/poll`, {
    method: 'POST',
  });
}

// ─── Activity ──────────────────────────────────────────────────────────────

export async function fetchActivity(params?: {
  limit?: number;
  cursor?: string;
}): Promise<ListActivityResponse> {
  const qs = new URLSearchParams();
  if (params?.limit) qs.set('limit', String(params.limit));
  if (params?.cursor) qs.set('cursor', params.cursor);
  const query = qs.toString();
  return fetchJson<ListActivityResponse>(
    `/api/activity${query ? `?${query}` : ''}`,
  );
}

export async function fetchActivityDetail(
  id: string,
): Promise<{ activity: TelegramActivity; insight: Insight | null }> {
  return fetchJson<{ activity: TelegramActivity; insight: Insight | null }>(
    `/api/activity/${id}`,
  );
}

// ─── Search Index ───────────────────────────────────────────────────────────

export async function fetchSearchIndex(): Promise<SearchIndexEntry[]> {
  try {
    return await fetchJson<SearchIndexEntry[]>('/api/search');
  } catch {
    return [];
  }
}

// ─── Chat ────────────────────────────────────────────────────────────────────

export interface ChatSource {
  insightId: string;
  title: string;
  category: string;
  relevanceScore: number;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
  sources?: ChatSource[];
}

export async function sendChatMessage(messages: ChatMessage[]): Promise<{ answer: string; sources: ChatSource[] }> {
  return fetchJson<{ answer: string; sources: ChatSource[] }>('/api/chat', {
    method: 'POST',
    body: JSON.stringify({ messages }),
  });
}

// ─── Ingest ──────────────────────────────────────────────────────────────────

export async function submitUrl(url: string): Promise<{ queued: number; skipped: number }> {
  return fetchJson<{ queued: number; skipped: number }>('/api/ingest', {
    method: 'POST',
    body: JSON.stringify({ url }),
  });
}

export async function submitUrls(urls: string[]): Promise<{ queued: number; skipped: number }> {
  return fetchJson<{ queued: number; skipped: number }>('/api/ingest', {
    method: 'POST',
    body: JSON.stringify({ urls }),
  });
}

export interface CrawlPreviewResult { url: string; alreadyIngested: boolean; }

export async function crawlPreview(url: string, maxPages: number): Promise<{ urls: CrawlPreviewResult[]; newCount: number; estimatedCost: string }> {
  return fetchJson<{ urls: CrawlPreviewResult[]; newCount: number; estimatedCost: string }>('/api/crawl/preview', {
    method: 'POST',
    body: JSON.stringify({ url, maxPages }),
  });
}

export async function crawlQueue(urls: string[]): Promise<{ queued: number }> {
  return fetchJson<{ queued: number }>('/api/crawl/queue', {
    method: 'POST',
    body: JSON.stringify({ urls }),
  });
}

// ─── Semantic Search ─────────────────────────────────────────────────────────

export interface SemanticSearchResult {
  insight: Insight;
  score: number;
}

export async function semanticSearch(q: string, limit = 12): Promise<SemanticSearchResult[]> {
  const data = await fetchJson<{ items: SemanticSearchResult[] }>(
    `/api/search/semantic?q=${encodeURIComponent(q)}&limit=${limit}`,
  );
  return data.items;
}

// ─── Export ───────────────────────────────────────────────────────────────────

export async function exportObsidian(): Promise<{ url: string; count: number }> {
  return fetchJson<{ url: string; count: number }>('/api/export/obsidian');
}

// ─── Digests ─────────────────────────────────────────────────────────────────

export async function fetchDigests(): Promise<DigestRecord[]> {
  try {
    return await fetchJson<DigestRecord[]>('/api/digests');
  } catch {
    return [];
  }
}

// ─── Advisor ─────────────────────────────────────────────────────────────────

export async function getAdvisorPlan(goal: string): Promise<AdvisorPlan> {
  return fetchJson<AdvisorPlan>('/api/advisor', {
    method: 'POST',
    body: JSON.stringify({ goal }),
  });
}

// ─── GitHub Repo Viewer ───────────────────────────────────────────────────────

export interface GithubRepoData {
  fullName: string;
  description: string | null;
  stars: number;
  forks: number;
  openIssues: number;
  language: string | null;
  topics: string[];
  homepage: string | null;
  updatedAt: string;
  repoUrl: string;
  readmeHtml: string | null;
}

export async function fetchGithubRepo(url: string): Promise<GithubRepoData> {
  return fetchJson<GithubRepoData>(`/api/github/repo?url=${encodeURIComponent(url)}`);
}
